import { lazy, Suspense, useEffect, useState } from "react";
import { salesDocPath } from "../api/client";
import { projects as projectsApi, type TimeEntry } from "../api/projects";
import type { CustomerDetail, Item } from "../api/types";
import type { Expense, Vendor } from "../api/types";
import type { SalesDoc } from "../api/sales-types";
import { useLoad } from "../api/useLoad";
import { t, tx } from "../i18n";
import { fmtMoney } from "../ui/currency/money";
import { Drawer, Loading } from "../ui/parts";
import { keptLookup } from "./offlineCache";
import { resendFixed, sendOrQueue, type OutboxItem } from "./outbox";

/*
 * Wave 9 (docs/implemented-offline9.md): the CRM's own full forms, opened where there is no connection.
 *
 *   FullFormDrawer   on the offline screen after a reload (PwaUi.tsx OfflineApp): new and edited customers, vendors, items,
 *                    expenses, draft invoices and estimates, time entries, and payments on kept invoices, with every field the
 *                    screens have (addresses, GST details, currencies, tags, custom fields, lines, receipt photos). They are
 *                    the same components the screens use, loaded from the app kept by the service worker; their lists (the
 *                    customers, items, currencies, price books, accounts, projects, custom field definitions) come from the
 *                    copies kept on this device (offlineCache.ts, kept at sign-in by keepLookups). What they save goes to the
 *                    outbox exactly as when the full CRM loses its connection, after the checks the server would make
 *                    (validate-core.ts).
 *   FixIssue         from the outbox: an invoice issued offline that the server refused for a reason the device can fix,
 *                    opened in the full invoice form, then sent again with the same number (outbox.ts resendFixed).
 */

const CustomerForm = lazy(() => import("../ui/workspaces/CustomersWorkspace").then((m) => ({ default: m.CustomerForm })));
const ItemForm = lazy(() => import("../ui/workspaces/ItemsWorkspace").then((m) => ({ default: m.ItemForm })));
const VendorFormDrawer = lazy(() => import("../ui/purchases/VendorDrawers").then((m) => ({ default: m.VendorFormDrawer })));
const ExpenseFormDrawer = lazy(() => import("../ui/purchases/ExpenseDrawers").then((m) => ({ default: m.ExpenseFormDrawer })));
const DocFormDrawer = lazy(() => import("../ui/sales/DocsWorkspace").then((m) => ({ default: m.DocFormDrawer })));
const PaymentDialog = lazy(() => import("../ui/sales/DocsWorkspace").then((m) => ({ default: m.PaymentDialog })));
const FixIssueDrawer = lazy(() => import("../ui/sales/DocsWorkspace").then((m) => ({ default: m.FixIssueDrawer })));
const EntryForm = lazy(() => import("../ui/projects/ProjectsWorkspace").then((m) => ({ default: m.EntryForm })));

export type FullForm =
  | { kind: "customer"; record?: CustomerDetail }
  | { kind: "vendor"; record?: Vendor }
  | { kind: "item"; record?: Item }
  | { kind: "expense"; record?: Expense }
  | { kind: "invoice" | "estimate"; record?: SalesDoc }
  | { kind: "time"; record?: TimeEntry }
  | { kind: "payment"; doc: SalesDoc };

const Wait = ({ onClose }: { onClose: () => void }) => (
  <Drawer open title={t("Opening the form…")} onClose={onClose}>
    <Loading />
  </Drawer>
);

/** One of the full forms, offline. `onDone` gets what to tell the person (kept for sending, or saved when the server answered). */
export function FullFormDrawer({ form, onClose, onDone }: { form: FullForm; onClose: () => void; onDone: (msg: string) => void }) {
  const saved = t("Saved.");
  const queued = (m?: string | null) => onDone(m ?? t("Kept on this device and sent once when the server answers again."));
  return <Suspense fallback={<Wait onClose={onClose} />}>{inner()}</Suspense>;
  function inner() {
    switch (form.kind) {
      case "customer":
        return <CustomerForm customer={form.record} onClose={onClose} onSaved={() => onDone(saved)} onQueued={queued} />;
      case "vendor":
        return <VendorFormDrawer vendor={form.record ?? null} onClose={onClose} onSaved={() => onDone(saved)} onQueued={queued} />;
      case "item":
        return <ItemForm item={form.record} onClose={onClose} onSaved={() => onDone(saved)} onQueued={queued} />;
      case "expense":
        return <ExpenseFormDrawer expense={form.record ?? null} onClose={onClose} onSaved={() => onDone(saved)} onQueued={queued} />;
      case "invoice":
      case "estimate":
        return <DocFormDrawer kind={form.kind} doc={form.record} onClose={onClose} onSaved={(_, m) => onDone(m ?? saved)} onQueued={queued} />;
      case "time":
        return <TimeForm entry={form.record} onClose={onClose} onDone={onDone} />;
      case "payment":
        return <PaymentForm doc={form.doc} onClose={onClose} onDone={onDone} />;
    }
  }
}

/** The time entry form needs the projects (kept at sign-in): the person's projects and their tasks. */
function TimeForm({ entry, onClose, onDone }: { entry?: TimeEntry; onClose: () => void; onDone: (msg: string) => void }) {
  const meta = useLoad(() => projectsApi.meta(), []);
  if (!meta.data) {
    return meta.error ? (
      <Drawer open title={t("Log time")} onClose={onClose}>
        <p className="crm-note">{t("Nothing kept for this yet: open {what} once while online, then it works here too.", { what: t("Projects") })}</p>
      </Drawer>
    ) : (
      <Wait onClose={onClose} />
    );
  }
  return <EntryForm meta={meta.data} entry={entry} onClose={onClose} onSaved={() => onDone(t("Saved."))} onQueued={(m) => onDone(m)} />;
}

/** A payment on a kept invoice, with the full payment form: through the outbox like the invoice screen's. */
function PaymentForm({ doc, onClose, onDone }: { doc: SalesDoc; onClose: () => void; onDone: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (body: { amount: string } & Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const label = t("Payment {amount} on {label} · {customer_name}", { amount: fmtMoney(body.amount, doc), label: tx(doc.label), customer_name: doc.customer_name });
      const r = await sendOrQueue<SalesDoc>({ kind: "payment", label, path: `${salesDocPath("invoice", doc.id)}/payments`, body });
      onDone(r.queued ? t("No connection: the payment is kept on this device and will be recorded when you're back online.") : t("Saved."));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return <PaymentDialog doc={doc} busy={busy} error={error} onClose={onClose} onSave={(b) => void save(b)} />;
}

/**
 * Wave 9: the full invoice form for an issue the server refused for a reason this device can fix. A new invoice opens with
 * what it was; a draft opens as kept on this device. Sending it again keeps the number.
 */
export function FixIssue({ item, onClose, onDone }: { item: OutboxItem; onClose: () => void; onDone: (msg: string) => void }) {
  const body = item.body as { draft?: Record<string, unknown>; number?: string };
  const number = item.fixable?.number ?? body.number ?? "";
  const [doc, setDoc] = useState<SalesDoc | null | undefined>(body.draft ? null : undefined);
  useEffect(() => {
    if (body.draft || !item.record) return;
    void keptLookup<SalesDoc>(item.record).then(setDoc, () => setDoc(null));
  }, [item.record, body.draft]);
  if (doc === undefined) return <Wait onClose={onClose} />;
  if (!body.draft && !doc)
    return (
      <Drawer open title={t("Fix the invoice")} onClose={onClose}>
        <p className="crm-note">{t("The draft is not kept on this device. Open it once while online to fix it, then send it again as {number}.", { number })}</p>
      </Drawer>
    );
  return (
    <Suspense fallback={<Wait onClose={onClose} />}>
      <FixIssueDrawer
        doc={doc ?? undefined}
        onClose={onClose}
        fix={{
          number,
          draft: body.draft,
          onFixed: (draft, shown, savedAt) => void resendFixed(item.id, draft, shown, savedAt).then(() => onDone(t("Sent again as {number}.", { number }))),
        }}
      />
    </Suspense>
  );
}
